import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Select } from '@/components/ui'
import { useRoulettes } from '@/hooks/roulette/useRoulettes'
import type { RouletteWithStats } from '@/types/roulette'

interface RouletteListProps {
  onCreateNew: () => void
  onViewHistory: (roulette: RouletteWithStats) => void
  onDeleteRoulette?: (roulette: RouletteWithStats) => void
  getRouletteProgress?: (roulette: RouletteWithStats) => {
    available: number
    total: number
    spun: number
    isCompleted: boolean
  }
}

export function RouletteList({
  onCreateNew,
  onViewHistory,
  onDeleteRoulette,
  getRouletteProgress,
}: RouletteListProps) {
  const { t } = useTranslation()
  const [sortBy, setSortBy] = useState<'name' | 'updated' | 'spins'>('updated')
  const { data: roulettes = [], isLoading, error } = useRoulettes()

  const sortedRoulettes = [...roulettes].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'spins':
        return b.total_spins - a.total_spins
      default:
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    }
  })

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-error mb-4">
          <svg
            className="mx-auto h-12 w-12 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-medium">{t('common.error')}</h3>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center">
          <div>
            <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-32 bg-muted rounded animate-pulse"></div>
        </div>

        {/* Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div
              key={`roulette-card-loading-${item}`}
              className="bg-card rounded-lg shadow-sm border border-border p-6"
            >
              <div className="h-6 w-3/4 bg-muted rounded animate-pulse mb-3"></div>
              <div className="h-4 w-full bg-muted rounded animate-pulse mb-4"></div>
              <div className="flex justify-between items-center mb-4">
                <div className="h-4 w-20 bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-16 bg-muted rounded animate-pulse"></div>
              </div>
              <div className="h-10 w-full bg-muted rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (roulettes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-muted-foreground mb-6">
          <div className="text-6xl mb-4">🎲</div>
        </div>
        <h3 className="text-xl font-medium text-foreground mb-2">{t('roulette.noRoulettesYet')}</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          {t('roulette.createFirstRouletteDescription')}
        </p>
        <Button
          onClick={onCreateNew}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg font-semibold"
        >
          🎲 {t('roulette.createFirstRoulette')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">🎲 {t('roulette.myRoulettes')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('roulette.manageRouletteCollections')} • {roulettes.length}{' '}
            {t('roulette.roulettesCount')}
          </p>
        </div>
        <Button
          onClick={onCreateNew}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
        >
          {t('roulette.createNew')}
        </Button>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">{t('common.sort')}:</span>
        <Select
          aria-label={t('common.sort')}
          size="sm"
          value={sortBy}
          onChange={setSortBy}
          className="w-auto min-w-[180px]"
          options={[
            { value: 'updated', label: t('roulette.sortByUpdated') },
            { value: 'name', label: t('roulette.sortByName') },
            { value: 'spins', label: t('roulette.sortBySpins') },
          ]}
        />
      </div>

      {/* Roulettes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedRoulettes.map((roulette) => (
          <RouletteCard
            key={roulette.id}
            roulette={roulette}
            onViewHistory={() => onViewHistory(roulette)}
            onDelete={onDeleteRoulette}
            getRouletteProgress={getRouletteProgress}
          />
        ))}
      </div>
    </div>
  )
}

// Individual Roulette Card Component
interface RouletteCardProps {
  roulette: RouletteWithStats
  onViewHistory: () => void
  onDelete?: (roulette: RouletteWithStats) => void
  getRouletteProgress?: (roulette: RouletteWithStats) => {
    available: number
    total: number
    spun: number
    isCompleted: boolean
  }
}

function RouletteCard({
  roulette,
  onViewHistory,
  onDelete,
  getRouletteProgress,
}: RouletteCardProps) {
  const { t } = useTranslation()

  // Get progress information
  const progress = getRouletteProgress
    ? getRouletteProgress(roulette)
    : {
        available: roulette.book_count,
        total: roulette.book_count,
        spun: 0,
        isCompleted: false,
      }

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString))
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border hover:shadow-md transition-shadow flex flex-col h-full">
      {/* Header */}
      <div className="p-6 pb-4 flex-1">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-xl"
              style={{ backgroundColor: `${roulette.color}20` }}
            >
              {roulette.icon}
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-lg">{roulette.name}</h3>
              {roulette.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {roulette.description}
                </p>
              )}
            </div>
          </div>

          {/* Delete Button */}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(roulette)}
              className="p-2 text-muted-foreground hover:text-error hover:bg-error/5 rounded-lg transition-colors"
              title={t('roulette.deleteRoulette')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="text-center p-3 bg-card border border-border rounded-lg shadow-sm">
            <div className="font-semibold text-lg text-card-foreground">{progress.available}</div>
            <div className="text-muted-foreground">{t('roulette.availableBooks')}</div>
          </div>
          <div className="text-center p-3 bg-card border border-border rounded-lg shadow-sm">
            <div className="font-semibold text-lg text-card-foreground">{progress.spun}</div>
            <div className="text-muted-foreground">{t('roulette.spinsCompleted')}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">{t('roulette.progress')}</span>
            <span className="text-xs text-muted-foreground">
              {progress.spun}/{progress.total}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                progress.isCompleted ? 'bg-success' : 'bg-primary'
              }`}
              style={{
                width: `${progress.total > 0 ? (progress.spun / progress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Status and Last Activity - Fixed height section */}
        <div className="mt-4 min-h-[80px] flex flex-col justify-between">
          {/* Status Section */}
          <div>
            {progress.isCompleted ? (
              <div className="p-3 bg-success/5 border border-success/20 rounded-lg">
                <div className="text-sm text-success font-medium mb-1">
                  ✅ {t('roulette.completed')}
                </div>
                <div className="text-sm text-success">{t('roulette.allBooksSpun')}</div>
              </div>
            ) : (
              roulette.current_reading && (
                <div className="p-3 bg-success/5 border border-success/20 rounded-lg">
                  <div className="text-sm text-success font-medium mb-1">
                    📖 {t('roulette.currentlyReading')}
                  </div>
                  <div className="text-sm text-success">{roulette.current_reading.title}</div>
                </div>
              )
            )}
          </div>

          {/* Last Activity */}
          <div className="mt-auto">
            {roulette.last_spin_at && (
              <div className="text-xs text-muted-foreground">
                {t('roulette.lastSpin')}: {formatDate(roulette.last_spin_at)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 pb-6">
        <Button
          onClick={onViewHistory}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
        >
          📊 {t('roulette.viewProgress')}
        </Button>
      </div>
    </div>
  )
}
